import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";

export function AuditLog() {
  const [searchParams] = useSearchParams();
  const machineId = searchParams.get("machine") ?? undefined;

  const { data: events, isLoading } = useQuery({
    queryKey: ["audit", machineId],
    queryFn: () => api.listAuditEvents(machineId),
  });

  const filteredMachineName = events?.find((e) => e.machine_id === machineId)?.machine_name;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Audit log</h1>
          <p className="text-sm text-muted-foreground">
            {machineId
              ? `Every connection, download, and admin action for "${filteredMachineName ?? "this device"}".`
              : "Every machine and admin action, newest first."}
          </p>
        </div>
        {machineId && (
          <Button variant="outline" size="sm" asChild>
            <Link to="/audit">Clear filter</Link>
          </Button>
        )}
      </div>

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {events?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No events yet.
                  </TableCell>
                </TableRow>
              )}
              {events?.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {new Date(event.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-medium">{event.action}</TableCell>
                  <TableCell>
                    {event.machine_id ? (
                      <Link
                        to={`/audit?machine=${event.machine_id}`}
                        className="text-sm text-foreground underline-offset-2 hover:underline"
                      >
                        {event.machine_name ?? event.machine_id}
                      </Link>
                    ) : event.admin_username ? (
                      <span className="text-sm text-muted-foreground">
                        {event.admin_username}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {Object.keys(event.detail).length > 0 ? JSON.stringify(event.detail) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
